import { 
  GitHubRepository, 
  GitHubBranch, 
  GitHubWebhook, 
  GitHubOrganization,
  FileInfo,
  WebhookEvent,
  PullRequest,
  Repository,
  RepositorySettings,
  RepositorySettingsWithDefaults,
  DeploymentLogs,
  ContainerLog,
  EnvironmentVariable,
  EnvironmentVariableRequest,
  BuildArgument,
  BuildArgumentRequest,
  DeploymentEvent
} from '../types'
import { apiClient } from '../utils/apiClient'
import { API_URL } from '../config'
import { LOGIN_ATTRIBUTION_LS_KEY, MAX_LOGIN_ATTRIBUTION_LEN } from './loginAttribution'

// GitHub OAuth and API service
class GitHubService {
  private apiBase: string
  private backendUrl: string

  constructor() {
    // Use environment variables for GitHub OAuth
    this.apiBase = 'https://api.github.com'
    this.backendUrl = API_URL
  }

  // Initiate GitHub OAuth flow
  async initiateOAuth(): Promise<void> {
    try {
      // Get current page URL to redirect back after authentication
      const redirectURL = window.location.origin + window.location.pathname + window.location.search

      const qs = new URLSearchParams()
      qs.set('redirect_url', redirectURL)
      const pending = localStorage.getItem(LOGIN_ATTRIBUTION_LS_KEY)?.trim()
      if (pending) {
        qs.set('login_attribution_code', pending.slice(0, MAX_LOGIN_ATTRIBUTION_LEN))
      }

      const data = await apiClient.get<{ auth_url: string }>(`/api/auth/github?${qs.toString()}`)
      
      if (data.auth_url) {
        // Redirect to GitHub OAuth
        window.location.href = data.auth_url
      } else {
        throw new Error('No auth URL in response')
      }
    } catch (error) {
      console.error('Error initiating OAuth:', error)
      throw error
    }
  }


  // Get all user's repositories
  async getRepositories(): Promise<GitHubRepository[]> {
    if (!this.shouldMakeApiCalls()) {
      console.warn('No GitHub access token found, skipping API call')
      return []
    }
    
    const token = this.getAccessToken()
    
    try {
      return await apiClient.get<GitHubRepository[]>('/api/repositories', {
        'Authorization': `Bearer ${token}`,
      })
    } catch (error) {
      console.error('Error fetching repositories:', error)
      throw error
    }
  }

  // Get all organizations the user has access to
  async getOrganizations(): Promise<GitHubOrganization[]> {
    if (!this.shouldMakeApiCalls()) {
      console.warn('No GitHub access token found, skipping API call')
      return []
    }
    
    const token = this.getAccessToken()
    
    try {
      return await apiClient.get<GitHubOrganization[]>('/api/organizations', {
        'Authorization': `Bearer ${token}`,
      })
    } catch (error) {
      console.error('Error fetching organizations:', error)
      throw error
    }
  }

  // Get all owners (user's own account + organizations) the user has access to
  async getOwners(): Promise<GitHubOrganization[]> {
    if (!this.shouldMakeApiCalls()) {
      console.warn('No GitHub access token found, skipping API call')
      return []
    }
    
    const token = this.getAccessToken()
    
    try {
      return await apiClient.get<GitHubOrganization[]>('/api/owners', {
        'Authorization': `Bearer ${token}`,
      })
    } catch (error) {
      console.error('Error fetching owners:', error)
      throw error
    }
  }

  // Get connected organizations
  async getConnectedOrganizations(): Promise<GitHubOrganization[]> {
    if (!this.shouldMakeApiCalls()) {
      console.warn('No GitHub access token found, skipping API call')
      return []
    }
    
    const token = this.getAccessToken()
    
    try {
      return await apiClient.get<GitHubOrganization[]>('/api/organizations/connected', {
        'Authorization': `Bearer ${token}`,
      })
    } catch (error) {
      console.error('Error fetching connected organizations:', error)
      throw error
    }
  }

  // Add a new organization
  async addOrganization(login: string): Promise<GitHubOrganization> {
    if (!this.shouldMakeApiCalls()) {
      console.warn('No GitHub access token found, skipping API call')
      throw new Error('No GitHub access token found')
    }
    
    const token = this.getAccessToken()
    
    try {
      return await apiClient.post<GitHubOrganization>('/api/organizations', { login }, {
        'Authorization': `Bearer ${token}`,
      })
    } catch (error) {
      console.error('Error adding organization:', error)
      throw error
    }
  }

  // Remove an organization
  async removeOrganization(id: number): Promise<void> {
    if (!this.shouldMakeApiCalls()) {
      console.warn('No GitHub access token found, skipping API call')
      throw new Error('No GitHub access token found')
    }
    
    const token = this.getAccessToken()
    
    try {
      await apiClient.delete(`/api/organizations/${id}`, {
        'Authorization': `Bearer ${token}`,
      })
    } catch (error) {
      console.error('Error removing organization:', error)
      throw error
    }
  }

  // Get repositories for a specific owner (user or organization)
  async getRepositoriesByOwner(owner: string): Promise<GitHubRepository[]> {
    if (!this.shouldMakeApiCalls()) {
      console.warn('No GitHub access token found, skipping API call')
      return []
    }
    
    const token = this.getAccessToken()
    
    try {
      return await apiClient.get<GitHubRepository[]>(`/api/repositories/owner/${owner}`, {
        'Authorization': `Bearer ${token}`,
      })
    } catch (error) {
      console.error('Error fetching repositories for owner:', error)
      throw error
    }
  }

  // Get only connected repositories
  async getConnectedRepositories(): Promise<GitHubRepository[]> {
    if (!this.shouldMakeApiCalls()) {
      console.warn('No GitHub access token found, skipping API call')
      return []
    }
    
    const token = this.getAccessToken()
    
    try {
      const data = await apiClient.get<GitHubRepository[]>('/api/repositories/connected', {
        'Authorization': `Bearer ${token}`,
      })
      // Ensure we always return an array, even if the API returns null/undefined
      return Array.isArray(data) ? data : []
    } catch (error) {
      console.error('Error fetching connected repositories:', error)
      // Return empty array on error instead of throwing
      return []
    }
  }

  // Save initial repository association
  async saveRepository(owner: string, repo: string): Promise<void> {
    if (!this.shouldMakeApiCalls()) {
      console.warn('No GitHub access token found, skipping API call')
      return
    }
    
    const token = this.getAccessToken()
    
    try {
      await apiClient.post('/api/repositories', { owner, repo }, {
        'Authorization': `Bearer ${token}`,
      })
    } catch (error) {
      console.error('Error saving repository:', error)
      throw error
    }
  }

  // Set up webhook for a repository
  async setupWebhook(owner: string, repo: string): Promise<GitHubWebhook> {
    if (!this.shouldMakeApiCalls()) {
      console.warn('No GitHub access token found, skipping API call')
      throw new Error('No GitHub access token found')
    }
    
    const token = this.getAccessToken()
    
    try {
      return await apiClient.post<GitHubWebhook>('/api/webhooks/setup', { owner, repo }, {
        'Authorization': `Bearer ${token}`,
      })
    } catch (error) {
      console.error('Error setting up webhook:', error)
      throw error
    }
  }

  async getRepositoryFiles(owner: string, repo: string, path: string = ''): Promise<FileInfo[]> {
    console.log('getRepositoryFiles called with:', { owner, repo, path })
    
    if (!this.shouldMakeApiCalls()) {
      console.warn('No GitHub access token found, skipping API call')
      return []
    }
    
    const token = this.getAccessToken()
    console.log('Got access token, making API call to:', `/api/files?owner=${owner}&repo=${repo}&path=${path}`)
    
    try {
      const result = await apiClient.get<FileInfo[]>(`/api/files?owner=${owner}&repo=${repo}&path=${path}`, {
        'Authorization': `Bearer ${token}`,
      })
      console.log('API call successful, result:', result)
      return result
    } catch (error) {
      console.error('Error fetching repository files:', error)
      throw error
    }
  }

  async updateComposeFilePath(owner: string, repo: string, path: string): Promise<void> {
    if (!this.shouldMakeApiCalls()) {
      console.warn('No GitHub access token found, skipping API call')
      throw new Error('No GitHub access token found')
    }
    
    const token = this.getAccessToken()
    
    try {
      // First get the repository ID
      const repos = await this.getConnectedRepositories()
      const targetRepo = repos.find(r => (r.full_name || (r as any).fullName) === `${owner}/${repo}`)
      
      if (!targetRepo) {
        throw new Error('Repository not found')
      }

      const response = await fetch(`${this.backendUrl}/api/repositories/${targetRepo.id}/compose-path`, {
        method: 'PUT',
        credentials: 'include',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ path }),
      })

      if (!response.ok) {
        const errorText = await response.text()
        console.error('API error response:', errorText)
        throw new Error(`Failed to update compose file path: ${response.status} ${response.statusText}`)
      }
    } catch (error) {
      console.error('Error updating compose file path:', error)
      throw error
    }
  }

  // Get external service for a repository
  async getExternalService(repositoryId: string): Promise<{ external_service: string }> {
    const token = this.getAccessToken()
    
    try {
      return await apiClient.get<{ external_service: string }>(`/api/repositories/${repositoryId}/external-service`, {
        'Authorization': `Bearer ${token}`,
      })
    } catch (error) {
      console.error('Error fetching external service:', error)
      throw error
    }
  }

  // Update external service for a repository
  async updateExternalService(repositoryId: string, externalService: string): Promise<void> {
    const token = this.getAccessToken()
    
    try {
      const response = await fetch(`${this.backendUrl}/api/repositories/${repositoryId}/external-service`, {
        method: 'PUT',
        credentials: 'include',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ external_service: externalService }),
      })

      if (!response.ok) {
        throw new Error('Failed to update external service')
      }
    } catch (error) {
      console.error('Error updating external service:', error)
      throw error
    }
  }

  // Get repository file content
  async getRepositoryFileContent(owner: string, repo: string, path: string): Promise<string> {
    if (!this.shouldMakeApiCalls()) {
      console.warn('No GitHub access token found, skipping API call')
      throw new Error('No GitHub access token found')
    }
    
    const token = this.getAccessToken()
    
    try {
      const response = await fetch(`${this.backendUrl}/api/files/content?owner=${owner}&repo=${repo}&path=${encodeURIComponent(path)}`, {
        credentials: 'include',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
        },
      })

      if (!response.ok) {
        // Try to parse error response for better error messages
        try {
          const errorData = await response.json()
          if (errorData.code === 'GITHUB_AUTH_EXPIRED') {
            throw new Error('GitHub authentication expired. Please sign in again.')
          }
          throw new Error(errorData.error || 'Failed to fetch file content')
        } catch (parseError) {
          throw new Error('Failed to fetch file content')
        }
      }

      const data = await response.json()
      return data.content
    } catch (error) {
      console.error('Error fetching file content:', error)
      throw error
    }
  }

  async getWebhookEvents(type?: string, since?: string): Promise<WebhookEvent[]> {
    const token = this.getAccessToken()
    
    try {
      let url = `${this.backendUrl}/api/webhooks/events`
      const params = new URLSearchParams()
      if (type) params.append('type', type)
      if (since) params.append('since', since)
      if (params.toString()) url += `?${params.toString()}`

      const response = await fetch(url, {
        credentials: 'include',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
        },
      })

      if (!response.ok) {
        throw new Error('Failed to fetch webhook events')
      }

      return await response.json()
    } catch (error) {
      console.error('Error fetching webhook events:', error)
      throw error
    }
  }

  // Verify webhook signature
  verifyWebhookSignature(_payload: string, _signature: string, _secret: string): boolean {
    // Note: This would need to be implemented on the server side
    // For client-side, we'll return true for demo purposes
    console.warn('Webhook signature verification should be done server-side')
    return true
  }

  // Get environment PRs for a repository
  async getEnvironmentPRs(id: string): Promise<PullRequest[]> {
    const token = this.getAccessToken()
    
    try {
      return await apiClient.get<PullRequest[]>(`/api/repositories/${id}/environment-prs`, {
        'Authorization': `Bearer ${token}`,
      })
    } catch (error) {
      console.error('Error fetching environment PRs:', error)
      throw error
    }
  }

  // Get environment PRs with preview URL information (DEPRECATED - use getPullRequests and getPreviewEnvironments)
  async getEnvironmentPRsWithPreview(id: string): Promise<PullRequest[]> {
    const token = this.getAccessToken()
    
    try {
      return await apiClient.get<PullRequest[]>(`/api/repositories/${id}/environment-prs-with-preview`, {
        'Authorization': `Bearer ${token}`,
      })
    } catch (error) {
      console.error('Error fetching environment PRs with preview:', error)
      throw error
    }
  }

  // New RESTful endpoints
  async getPullRequests(repositoryId: string): Promise<PullRequest[]> {
    const token = this.getAccessToken()
    
    try {
      const response = await apiClient.get<any[]>(`/api/repositories/${repositoryId}/pull-requests`, {
        'Authorization': `Bearer ${token}`,
      })
      
      // Transform snake_case API response to camelCase for frontend
      return (response || []).map((pr: any): PullRequest => ({
        ID: pr.id,
        CreatedAt: pr.created_at,
        UpdatedAt: pr.updated_at,
        DeletedAt: pr.deleted_at,
        RepositoryID: pr.repository_id,
        Number: pr.number,
        Title: pr.title,
        Branch: pr.branch,
        Author: pr.author,
        LatestCommitSHA: pr.latest_commit_sha,
        HasEnvComment: pr.has_env_comment,
        LastEnvComment: pr.last_env_comment,
        LastEnvCommentURL: pr.last_env_comment_url,
        LastEnvCommentAt: pr.last_env_comment_at,
        PreviewEnvironmentID: pr.preview_environment_id,
        URL: pr.url ?? '',
        State: pr.state,
        PreviewExists: pr.preview_exists,
        PreviewURL: pr.url, // Environment URL (slug-based)
        Namespace: pr.namespace,
        Status: pr.status,
        LastCommitSHA: pr.last_commit_sha,
        DeploymentCount: pr.deployment_count,
        lastDeploymentDuration: pr.last_deployment_duration,
        last_deployment_duration_seconds: pr.last_deployment_duration_seconds
      }))
    } catch (error) {
      console.error('Error fetching pull requests:', error)
      throw error
    }
  }

  // Refresh branches from GitHub (sync with GitHub API)
  async refreshBranches(repositoryId: string): Promise<void> {
    const token = this.getAccessToken()

    try {
      await apiClient.post(`/api/repositories/${repositoryId}/branches/refresh`, {}, {
        'Authorization': `Bearer ${token}`,
      })
    } catch (error) {
      console.error('Error refreshing branches:', error)
      throw error
    }
  }

  // Refresh pull requests from GitHub (sync with GitHub API)
  async refreshPullRequests(repositoryId: string): Promise<void> {
    const token = this.getAccessToken()
    
    try {
      await apiClient.post(`/api/repositories/${repositoryId}/pull-requests/refresh`, {}, {
        'Authorization': `Bearer ${token}`,
      })
    } catch (error) {
      console.error('Error refreshing pull requests:', error)
      throw error
    }
  }

  async getPreviewEnvironments(repositoryId: string): Promise<any[]> {
    const token = this.getAccessToken()
    
    try {
      return await apiClient.get<any[]>(`/api/repositories/${repositoryId}/environments`, {
        'Authorization': `Bearer ${token}`,
      })
    } catch (error) {
      console.error('Error fetching preview environments:', error)
      throw error
    }
  }

  // Get repository details by ID
  async getRepositoryDetails(id: string): Promise<Repository> {
    if (!this.shouldMakeApiCalls()) {
      console.warn('No GitHub access token found, skipping API call')
      throw new Error('No GitHub access token found')
    }
    
    const token = this.getAccessToken()
    
    try {
      return await apiClient.get<Repository>(`/api/repositories/${id}`, {
        'Authorization': `Bearer ${token}`,
      })
    } catch (error) {
      console.error('Error fetching repository details:', error)
      throw error
    }
  }

  // Get repository settings
  async getRepositorySettings(id: string): Promise<RepositorySettings> {
    if (!this.shouldMakeApiCalls()) {
      console.warn('No GitHub access token found, skipping API call')
      throw new Error('No GitHub access token found')
    }
    
    const token = this.getAccessToken()
    
    try {
      return await apiClient.get<RepositorySettings>(`/api/repositories/${id}/settings`, {
        'Authorization': `Bearer ${token}`,
      })
    } catch (error) {
      console.error('Error fetching repository settings:', error)
      throw error
    }
  }

  // Get repository settings with defaults
  async getRepositorySettingsWithDefaults(id: string): Promise<RepositorySettingsWithDefaults> {
    if (!this.shouldMakeApiCalls()) {
      console.warn('No GitHub access token found, skipping API call')
      throw new Error('No GitHub access token found')
    }
    
    const token = this.getAccessToken()
    
    try {
      return await apiClient.get<RepositorySettingsWithDefaults>(`/api/repositories/${id}/settings-with-defaults`, {
        'Authorization': `Bearer ${token}`,
      })
    } catch (error) {
      console.error('Error fetching repository settings with defaults:', error)
      throw error
    }
  }

  // Update repository settings
  async updateRepositorySettings(id: string, settings: RepositorySettings): Promise<void> {
    if (!this.shouldMakeApiCalls()) {
      console.warn('No GitHub access token found, skipping API call')
      throw new Error('No GitHub access token found')
    }
    
    const token = this.getAccessToken()
    
    try {
      await apiClient.put(`/api/repositories/${id}/settings`, settings, {
        'Authorization': `Bearer ${token}`,
      })
    } catch (error) {
      console.error('Error updating repository settings:', error)
      throw error
    }
  }

  // Get deployments for a repository, optionally filtered by environment ID
  async getDeployments(
    repositoryId: string, 
    environmentId?: string,
    page?: number,
    limit?: number
  ): Promise<{ data: DeploymentLogs[]; pagination: { page: number; limit: number; total: number; total_pages: number } }> {
    const token = this.getAccessToken()
    
    try {
      let url = `/api/repositories/${repositoryId}/deployments`
      const params = new URLSearchParams()
      
      if (environmentId) {
        params.append('environment_id', environmentId)
      }
      if (page !== undefined) {
        params.append('page', page.toString())
      }
      if (limit !== undefined) {
        params.append('limit', limit.toString())
      }
      
      if (params.toString()) {
        url += `?${params.toString()}`
      }
      
      return await apiClient.get<{ data: DeploymentLogs[]; pagination: { page: number; limit: number; total: number; total_pages: number } }>(url, {
        'Authorization': `Bearer ${token}`,
      })
    } catch (error) {
      console.error('Error fetching deployments:', error)
      throw error
    }
  }

  async getDeploymentEvents(deploymentId: string): Promise<{ deployment_id: number; events: DeploymentEvent[] }> {
    const token = this.getAccessToken()
    
    try {
      return await apiClient.get<{ deployment_id: number; events: DeploymentEvent[] }>(`/api/deployments/${deploymentId}/events`, {
        'Authorization': `Bearer ${token}`,
      })
    } catch (error) {
      console.error('Error fetching deployment events:', error)
      throw error
    }
  }

  // Get detailed deployment status with state transition durations
  async getDeploymentStatus(deploymentId: string): Promise<{
    deployment_id: number;
    status: string;
    created_at: string;
    started_at?: string;
    completed_at?: string;
    duration_ms: number;
    state_transitions: Array<{
      from_state: string;
      to_state: string;
      started_at: string;
      completed_at?: string;
      duration_ms: number;
      is_current: boolean;
    }>;
  }> {
    const token = this.getAccessToken()
    
    try {
      return await apiClient.get(`/internal/deployments/${deploymentId}/status`, {
        'Authorization': `Bearer ${token}`,
      })
    } catch (error) {
      console.error('Error fetching deployment status:', error)
      throw error
    }
  }

  // Unlink/delete a pull request environment
  async unlinkPullRequest(_repositoryId: string, environmentId: string): Promise<{ message: string }> {
    const token = this.getAccessToken()
    
    if (!token) {
      throw new Error('No access token available')
    }
    
    try {
      console.log(`Making DELETE request to: /api/environments/${environmentId}`)
      const result = await apiClient.delete<{ message: string }>(`/api/environments/${environmentId}`, {
        'Authorization': `Bearer ${token}`,
      })
      console.log('Unlink PR API response:', result)
      return result
    } catch (error) {
      console.error('Error unlinking pull request:', error)
      throw error
    }
  }

  // Get all pods with logs (log monitoring)
  async getLogPods(): Promise<any[]> {
    const token = this.getAccessToken()
    
    try {
      return await apiClient.get<any[]>(`/api/logs/pods`, {
        'Authorization': `Bearer ${token}`,
      })
    } catch (error) {
      console.error('Error fetching pods:', error)
      throw error
    }
  }

  // Get all namespaces (log monitoring)
  async getLogNamespaces(): Promise<string[]> {
    const token = this.getAccessToken()
    
    try {
      return await apiClient.get<string[]>('/admin/log/namespaces', {
        'Authorization': `Bearer ${token}`,
      })
    } catch (error) {
      console.error('Error fetching namespaces:', error)
      throw error
    }
  }

  // Get services for a specific namespace (log monitoring)
  async getLogServices(namespace: string): Promise<any[]> {
    const token = this.getAccessToken()
    
    try {
      return await apiClient.get<any[]>(`/admin/log/namespaces/${namespace}/services`, {
        'Authorization': `Bearer ${token}`,
      })
    } catch (error) {
      console.error('Error fetching services:', error)
      throw error
    }
  }

  // Get aggregated logs for a specific service
  async getServiceLogs(namespace: string, serviceName: string): Promise<any[]> {
    const token = this.getAccessToken()
    
    try {
      return await apiClient.get<any[]>(`/api/logs/service-logs?namespace=${namespace}&service=${serviceName}`, {
        'Authorization': `Bearer ${token}`,
      })
    } catch (error) {
      console.error('Error fetching service logs:', error)
      throw error
    }
  }

  // Get logs for a specific pod
  async getPodLogs(podKey: string): Promise<any[]> {
    const token = this.getAccessToken()
    
    try {
      return await apiClient.get<any[]>(`/api/logs/pod/${podKey}`, {
        'Authorization': `Bearer ${token}`,
      })
    } catch (error) {
      console.error('Error fetching pod logs:', error)
      throw error
    }
  }

  // Get deployment logs with system events
  async getDeploymentLogsWithEvents(deploymentId: number, logType: 'build' | 'application' = 'application', serviceFilter?: string): Promise<{
    logs: any[]
    system_logs: any[]
    services: string[]
  }> {
    const token = this.getAccessToken()
    
    try {
      const params = new URLSearchParams({
        log_type: logType,
        line_count_limit: '100'
      })
      
      if (serviceFilter) {
        params.append('service_filter', serviceFilter)
      }

      return await apiClient.get<{
        logs: any[]
        system_logs: any[]
        services: string[]
      }>(`/api/deployments/${deploymentId}/logs?${params}`, {
        'Authorization': `Bearer ${token}`,
      })
    } catch (error) {
      console.error('Error fetching deployment logs:', error)
      throw error
    }
  }

  // Get deployment logs for a specific deployment
  async getDeploymentLogs(_repositoryId: string, deploymentId: string): Promise<DeploymentLogs> {
    const token = this.getAccessToken()
    
    try {
      return await apiClient.get<DeploymentLogs>(`/api/deployments/${deploymentId}/logs`, {
        'Authorization': `Bearer ${token}`,
      })
    } catch (error) {
      console.error('Error fetching deployment logs:', error)
      throw error
    }
  }

  // Get container logs for a specific container
  async getContainerLogs(_repositoryId: string, deploymentId: string, containerName: string): Promise<ContainerLog> {
    const token = this.getAccessToken()
    
    try {
      return await apiClient.get<ContainerLog>(`/api/deployments/${deploymentId}/containers/${containerName}/logs`, {
        'Authorization': `Bearer ${token}`,
      })
    } catch (error) {
      console.error('Error fetching container logs:', error)
      throw error
    }
  }

  // Get auto-detection status for a repository
  async getAutoDetectionStatus(id: string): Promise<{
    composeFilePath: string
    envPRCount: number
    autoDetected: boolean
  }> {
    const token = this.getAccessToken()
    
    try {
      return await apiClient.get<{
        composeFilePath: string
        envPRCount: number
        autoDetected: boolean
      }>(`/api/repositories/${id}/auto-detection-status`, {
        'Authorization': `Bearer ${token}`,
      })
    } catch (error) {
      console.error('Error fetching auto-detection status:', error)
      throw error
    }
  }

  // Delete preview environment by environment ID
  async deletePreviewEnvironment(_repoId: string, environmentId: string): Promise<void> {
    const token = this.getAccessToken()
    
    try {
      await apiClient.delete(`/api/environments/${environmentId}`, {
        'Authorization': `Bearer ${token}`,
      })
    } catch (error) {
      console.error('Error deleting preview environment:', error)
      throw error
    }
  }

  // Get repository details from GitHub API
  async getRepository(owner: string, repo: string): Promise<GitHubRepository> {
    const token = this.getAccessToken()
    
    try {
      const response = await fetch(`${this.apiBase}/repos/${owner}/${repo}`, {
        headers: {
          'Authorization': `token ${token}`,
          'Accept': 'application/vnd.github.v3+json',
        },
      })

      if (!response.ok) {
        throw new Error('Failed to fetch repository')
      }

      return await response.json()
    } catch (error) {
      console.error('Error fetching repository:', error)
      throw error
    }
  }

  // Get branches for a repository
  async getBranches(owner: string, repo: string): Promise<GitHubBranch[]> {
    const token = this.getAccessToken()
    
    try {
      const response = await fetch(`${this.apiBase}/repos/${owner}/${repo}/branches`, {
        headers: {
          'Authorization': `token ${token}`,
          'Accept': 'application/vnd.github.v3+json',
        },
      })

      if (!response.ok) {
        throw new Error('Failed to fetch branches')
      }

      return await response.json()
    } catch (error) {
      console.error('Error fetching branches:', error)
      throw error
    }
  }

  // Check if user has access to repository
  async checkRepositoryAccess(owner: string, repo: string): Promise<boolean> {
    try {
      await this.getRepository(owner, repo)
      return true
    } catch (error) {
      return false
    }
  }

  // Parse repository URL
  parseRepositoryUrl(url: string): { owner: string; repo: string } {
    const match = url.match(/github\.com\/([^\/]+)\/([^\/]+)/)
    if (match) {
      return {
        owner: match[1],
        repo: match[2].replace('.git', ''),
      }
    }
    throw new Error('Invalid GitHub repository URL')
  }

  // Get stored access token
  private getAccessToken(): string {
    const token = localStorage.getItem('github_access_token')
    if (!token) {
      localStorage.clear() // Clear any stale data
      throw new Error('No GitHub access token found')
    }
    return token
  }

  // Check if user is authenticated
  isAuthenticated(): boolean {
    return !!localStorage.getItem('github_access_token')
  }

  // Check if we should make API calls (has token)
  shouldMakeApiCalls(): boolean {
    return !!localStorage.getItem('github_access_token')
  }

  // Logout
  logout(): void {
    localStorage.removeItem('github_access_token')
  }

  // Environment Variables API methods
  async getEnvironmentVariables(repositoryId: number): Promise<EnvironmentVariable[]> {
    if (!this.shouldMakeApiCalls()) {
      console.warn('No GitHub access token found, skipping API call')
      return []
    }
    
    const token = this.getAccessToken()
    
    try {
      return await apiClient.get<EnvironmentVariable[]>(`/api/repositories/${repositoryId}/environment-variables`, {
        'Authorization': `Bearer ${token}`,
      })
    } catch (error) {
      console.error('Error fetching environment variables:', error)
      throw error
    }
  }

  async createEnvironmentVariable(repositoryId: number, envVar: EnvironmentVariableRequest): Promise<EnvironmentVariable> {
    if (!this.shouldMakeApiCalls()) {
      throw new Error('No GitHub access token found')
    }
    
    const token = this.getAccessToken()
    
    try {
      return await apiClient.post<EnvironmentVariable>(`/api/repositories/${repositoryId}/environment-variables`, envVar, {
        'Authorization': `Bearer ${token}`,
      })
    } catch (error) {
      console.error('Error creating environment variable:', error)
      throw error
    }
  }

  async updateEnvironmentVariable(repositoryId: number, envVarId: number, envVar: EnvironmentVariableRequest): Promise<EnvironmentVariable> {
    if (!this.shouldMakeApiCalls()) {
      throw new Error('No GitHub access token found')
    }
    
    const token = this.getAccessToken()
    
    try {
      return await apiClient.put<EnvironmentVariable>(`/api/repositories/${repositoryId}/environment-variables/${envVarId}`, envVar, {
        'Authorization': `Bearer ${token}`,
      })
    } catch (error) {
      console.error('Error updating environment variable:', error)
      throw error
    }
  }

  async deleteEnvironmentVariable(repositoryId: number, envVarId: number): Promise<void> {
    if (!this.shouldMakeApiCalls()) {
      throw new Error('No GitHub access token found')
    }
    
    const token = this.getAccessToken()
    
    try {
      await apiClient.delete(`/api/repositories/${repositoryId}/environment-variables/${envVarId}`, {
        'Authorization': `Bearer ${token}`,
      })
    } catch (error) {
      console.error('Error deleting environment variable:', error)
      throw error
    }
  }

  // Build Arguments API methods
  async getBuildArguments(repositoryId: number): Promise<BuildArgument[]> {
    if (!this.shouldMakeApiCalls()) {
      console.warn('No GitHub access token found, skipping API call')
      return []
    }
    const token = this.getAccessToken()
    try {
      return await apiClient.get<BuildArgument[]>(`/api/repositories/${repositoryId}/build-arguments`, {
        'Authorization': `Bearer ${token}`,
      })
    } catch (error) {
      console.error('Error fetching build arguments:', error)
      throw error
    }
  }

  async createBuildArgument(repositoryId: number, buildArg: BuildArgumentRequest): Promise<BuildArgument> {
    if (!this.shouldMakeApiCalls()) {
      throw new Error('No GitHub access token found')
    }
    const token = this.getAccessToken()
    try {
      return await apiClient.post<BuildArgument>(`/api/repositories/${repositoryId}/build-arguments`, buildArg, {
        'Authorization': `Bearer ${token}`,
      })
    } catch (error) {
      console.error('Error creating build argument:', error)
      throw error
    }
  }

  async updateBuildArgument(repositoryId: number, buildArgId: number, buildArg: BuildArgumentRequest): Promise<BuildArgument> {
    if (!this.shouldMakeApiCalls()) {
      throw new Error('No GitHub access token found')
    }
    const token = this.getAccessToken()
    try {
      return await apiClient.put<BuildArgument>(`/api/repositories/${repositoryId}/build-arguments/${buildArgId}`, buildArg, {
        'Authorization': `Bearer ${token}`,
      })
    } catch (error) {
      console.error('Error updating build argument:', error)
      throw error
    }
  }

  async deleteBuildArgument(repositoryId: number, buildArgId: number): Promise<void> {
    if (!this.shouldMakeApiCalls()) {
      throw new Error('No GitHub access token found')
    }
    const token = this.getAccessToken()
    try {
      await apiClient.delete(`/api/repositories/${repositoryId}/build-arguments/${buildArgId}`, {
        'Authorization': `Bearer ${token}`,
      })
    } catch (error) {
      console.error('Error deleting build argument:', error)
      throw error
    }
  }

  // Webhook management methods
  async checkWebhookStatus(repositoryId: string): Promise<{
    status: {
      configured: boolean
      webhook_id?: number
      url?: string
      secret?: string
      events?: string[]
      active?: boolean
      issues: string[]
      last_delivered?: string
      last_response?: string
    }
    expected: {
      url: string
      events: string[]
      secret: string
    }
  }> {
    if (!this.shouldMakeApiCalls()) {
      throw new Error('No GitHub access token found')
    }
    
    const token = this.getAccessToken()
    
    try {
      return await apiClient.get(`/api/repositories/${repositoryId}/webhooks/status`, {
        'Authorization': `Bearer ${token}`,
      })
    } catch (error) {
      console.error('Error checking webhook status:', error)
      throw error
    }
  }

  async fixWebhook(repositoryId: string): Promise<{
    message: string
    webhook_id: number
  }> {
    if (!this.shouldMakeApiCalls()) {
      throw new Error('No GitHub access token found')
    }
    
    const token = this.getAccessToken()
    
    try {
      return await apiClient.post(`/api/repositories/${repositoryId}/webhooks/fix`, {}, {
        'Authorization': `Bearer ${token}`,
      })
    } catch (error) {
      console.error('Error fixing webhook:', error)
      throw error
    }
  }

  // Cancel a deployment - moved to internal API service
  async cancelDeployment(environmentId: string): Promise<void> {
    console.log('DEBUG: cancelDeployment called with environmentId:', environmentId)
    
    const token = this.getAccessToken()
    if (!token) {
      console.error('DEBUG: No GitHub access token found')
      throw new Error('No GitHub access token found')
    }
    
    console.log('DEBUG: Using token:', token.substring(0, 10) + '...')
    
    const url = `/api/environments/${environmentId}`
    console.log('DEBUG: Making PATCH request to:', url)
    
    try {
      const response = await apiClient.patch(url, { action: 'cancel_deployment' }, {
        'Authorization': `Bearer ${token}`,
      })
      console.log('DEBUG: Cancel deployment response:', response)
    } catch (error: any) {
      console.error('DEBUG: Error cancelling deployment:', error)
      console.error('DEBUG: Error details:', {
        message: error?.message,
        status: error?.status,
        url: url,
        environmentId: environmentId
      })
      throw error
    }
  }
}

const githubService = new GitHubService()

export const useGitHubService = () => {
  return githubService
}

export default githubService
